import type {
  CanvasChangeBatch,
  CanvasCommand,
  CanvasEditor,
} from "../controller/types";
import {
  cloneJsonRecord,
  createCanvasEdge,
  createCanvasId,
  createCanvasNode,
  createEmptyCanvas,
} from "../domain/canvas";
import type {
  CanvasEdge,
  CanvasNode,
  CanvasSelection,
  DomainCanvas,
  EntityId,
} from "../domain/types";
import { assertValidCanvas } from "../validation";

export const CANVAS_CLIPBOARD_FORMAT =
  "application/vnd.note-app.canvas-selection+json";
export const CANVAS_CLIPBOARD_VERSION = 1;

export interface CanvasClipboardPayload {
  format: typeof CANVAS_CLIPBOARD_FORMAT;
  version: typeof CANVAS_CLIPBOARD_VERSION;
  nodes: CanvasNode[];
  edges: CanvasEdge[];
}

function selectedNodeIds(
  canvas: DomainCanvas,
  selection: CanvasSelection,
): Set<EntityId> {
  const ids = new Set(selection.nodeIds);
  for (const edge of canvas.edges) {
    if (selection.edgeIds.has(edge.id)) {
      ids.add(edge.sourceNodeId);
      ids.add(edge.targetNodeId);
    }
  }

  let changed = true;
  while (changed) {
    changed = false;
    const selectedGroupIds = new Set(
      canvas.nodes.flatMap((node) =>
        ids.has(node.id) && typeof node.props.groupId === "string"
          ? [node.props.groupId]
          : [],
      ),
    );
    for (const node of canvas.nodes) {
      const belongsToSelectedFrame =
        node.parentFrameId !== undefined && ids.has(node.parentFrameId);
      const belongsToSelectedGroup =
        typeof node.props.groupId === "string" &&
        selectedGroupIds.has(node.props.groupId);
      if (
        !ids.has(node.id) &&
        (belongsToSelectedFrame || belongsToSelectedGroup)
      ) {
        ids.add(node.id);
        changed = true;
      }
    }
  }
  return ids;
}

export function createCanvasClipboardPayload(
  canvas: DomainCanvas,
  selection: CanvasSelection,
): CanvasClipboardPayload | null {
  const ids = selectedNodeIds(canvas, selection);
  if (ids.size === 0) return null;
  const nodes = canvas.nodes
    .filter((node) => ids.has(node.id))
    .map((node): CanvasNode => {
      const clone = {
        ...node,
        geometry: { ...node.geometry },
        props: cloneJsonRecord(node.props),
        ...(node.reference === undefined
          ? {}
          : { reference: { ...node.reference } }),
      };
      if (clone.parentFrameId !== undefined && !ids.has(clone.parentFrameId)) {
        delete clone.parentFrameId;
      }
      return clone;
    });
  const edges = canvas.edges
    .filter((edge) => ids.has(edge.sourceNodeId) && ids.has(edge.targetNodeId))
    .map((edge) => ({
      ...edge,
      props: cloneJsonRecord(edge.props),
    }));
  return {
    format: CANVAS_CLIPBOARD_FORMAT,
    version: CANVAS_CLIPBOARD_VERSION,
    nodes,
    edges,
  };
}

export function serializeCanvasClipboard(
  payload: CanvasClipboardPayload,
): string {
  return JSON.stringify(payload);
}

export function deserializeCanvasClipboard(
  serialized: string,
): CanvasClipboardPayload | null {
  try {
    const value = JSON.parse(serialized) as Partial<CanvasClipboardPayload>;
    if (
      value.format !== CANVAS_CLIPBOARD_FORMAT ||
      value.version !== CANVAS_CLIPBOARD_VERSION ||
      !Array.isArray(value.nodes) ||
      !Array.isArray(value.edges)
    ) {
      return null;
    }
    const validated = assertValidCanvas({
      ...createEmptyCanvas(),
      nodes: value.nodes,
      edges: value.edges,
    });
    return createCanvasClipboardPayload(validated, {
      nodeIds: new Set(validated.nodes.map((node) => node.id)),
      edgeIds: new Set(validated.edges.map((edge) => edge.id)),
    });
  } catch {
    return null;
  }
}

export interface PasteCanvasClipboardOptions {
  offset?: number;
}

export function pasteCanvasClipboard(
  editor: CanvasEditor,
  payload: CanvasClipboardPayload,
  options: PasteCanvasClipboardOptions = {},
): CanvasChangeBatch | null {
  const offset = options.offset ?? 24;
  const nodeIdMap = new Map(
    payload.nodes.map((node) => [node.id, createCanvasId("node")]),
  );
  const groupIdMap = new Map<EntityId, EntityId>();
  const pasteToken = createCanvasId("paste");
  const parentFrameByNewNodeId = new Map<EntityId, EntityId>();
  const nodes = payload.nodes.map((node, index) => {
    const nodeId = nodeIdMap.get(node.id);
    if (nodeId === undefined) {
      throw new Error(`Missing clipboard node mapping for ${node.id}`);
    }
    const groupId =
      typeof node.props.groupId === "string"
        ? (groupIdMap.get(node.props.groupId) ??
          (() => {
            const next = createCanvasId("group");
            groupIdMap.set(node.props.groupId as string, next);
            return next;
          })())
        : undefined;
    const props = cloneJsonRecord(node.props);
    if (groupId === undefined) delete props.groupId;
    else props.groupId = groupId;
    const parentFrameId =
      node.parentFrameId === undefined
        ? undefined
        : nodeIdMap.get(node.parentFrameId);
    const created = createCanvasNode({
      id: nodeId,
      type: node.type,
      geometry: {
        ...node.geometry,
        x: node.geometry.x + offset,
        y: node.geometry.y + offset,
        zIndex: `${node.geometry.zIndex}~${pasteToken}-${index
          .toString()
          .padStart(4, "0")}`,
      },
      props,
      ...(node.reference === undefined
        ? {}
        : { reference: { ...node.reference } }),
      ...(node.readingOrder === undefined
        ? {}
        : { readingOrder: node.readingOrder }),
    });
    if (parentFrameId !== undefined) {
      parentFrameByNewNodeId.set(created.id, parentFrameId);
    }
    return created;
  });
  const edges = payload.edges.flatMap((edge) => {
    const sourceNodeId = nodeIdMap.get(edge.sourceNodeId);
    const targetNodeId = nodeIdMap.get(edge.targetNodeId);
    if (sourceNodeId === undefined || targetNodeId === undefined) return [];
    return [
      createCanvasEdge({
        id: createCanvasId("edge"),
        sourceNodeId,
        targetNodeId,
        ...(edge.relationType === undefined
          ? {}
          : { relationType: edge.relationType }),
        ...(edge.label === undefined ? {} : { label: edge.label }),
        props: cloneJsonRecord(edge.props),
      }),
    ];
  });
  const commands: Exclude<CanvasCommand, { type: "batch" }>[] = [
    ...nodes.map((node) => ({ type: "create-node" as const, node })),
    ...[...parentFrameByNewNodeId].map(([id, frameId]) => ({
      type: "move-into-frame" as const,
      nodeIds: [id],
      frameId,
    })),
    ...edges.map((edge) => ({ type: "create-edge" as const, edge })),
  ];
  if (commands.length === 0) return null;
  const batch = editor.dispatch({ type: "batch", commands });
  if (batch !== null) {
    editor.setSelection({
      nodeIds: nodes.map((node) => node.id),
      edgeIds: edges.map((edge) => edge.id),
      ...(nodes[0] === undefined ? {} : { primaryId: nodes[0].id }),
    });
  }
  return batch;
}

export function duplicateCanvasSelection(
  editor: CanvasEditor,
  options?: PasteCanvasClipboardOptions,
): CanvasChangeBatch | null {
  const snapshot = editor.getSnapshot();
  if (snapshot.readOnly) return null;
  const payload = createCanvasClipboardPayload(
    snapshot.canvas,
    snapshot.selection,
  );
  return payload === null
    ? null
    : pasteCanvasClipboard(editor, payload, options);
}

export function fitCanvasToScreen(editor: CanvasEditor, padding = 48): void {
  const snapshot = editor.getSnapshot();
  fitCanvasNodes(editor, snapshot.canvas.nodes, padding);
}

export function fitCanvasSelection(editor: CanvasEditor, padding = 64): void {
  const snapshot = editor.getSnapshot();
  const nodeIds = new Set(snapshot.selection.nodeIds);
  for (const edge of snapshot.canvas.edges) {
    if (!snapshot.selection.edgeIds.has(edge.id)) continue;
    nodeIds.add(edge.sourceNodeId);
    nodeIds.add(edge.targetNodeId);
  }
  fitCanvasNodes(
    editor,
    snapshot.canvas.nodes.filter((node) => nodeIds.has(node.id)),
    padding,
  );
}

function fitCanvasNodes(
  editor: CanvasEditor,
  nodes: readonly CanvasNode[],
  padding: number,
): void {
  const snapshot = editor.getSnapshot();
  const { viewportWidth, viewportHeight } = snapshot.view;
  if (
    nodes.length === 0 ||
    viewportWidth <= 0 ||
    viewportHeight <= 0 ||
    !Number.isFinite(padding)
  ) {
    return;
  }
  const bounds = nodes.map((node) => {
    const angle = (node.geometry.rotation * Math.PI) / 180;
    const centerX = node.geometry.x + node.geometry.width / 2;
    const centerY = node.geometry.y + node.geometry.height / 2;
    const extentX =
      (Math.abs(Math.cos(angle)) * node.geometry.width +
        Math.abs(Math.sin(angle)) * node.geometry.height) /
      2;
    const extentY =
      (Math.abs(Math.sin(angle)) * node.geometry.width +
        Math.abs(Math.cos(angle)) * node.geometry.height) /
      2;
    return {
      minX: centerX - extentX,
      minY: centerY - extentY,
      maxX: centerX + extentX,
      maxY: centerY + extentY,
    };
  });
  const minX = Math.min(...bounds.map((value) => value.minX));
  const minY = Math.min(...bounds.map((value) => value.minY));
  const maxX = Math.max(...bounds.map((value) => value.maxX));
  const maxY = Math.max(...bounds.map((value) => value.maxY));
  const contentWidth = Math.max(1, maxX - minX);
  const contentHeight = Math.max(1, maxY - minY);
  const safePadding = Math.max(
    0,
    Math.min(padding, viewportWidth / 2 - 1, viewportHeight / 2 - 1),
  );
  const zoom = Math.min(
    (viewportWidth - safePadding * 2) / contentWidth,
    (viewportHeight - safePadding * 2) / contentHeight,
  );
  editor.zoomAt(0, 0, zoom);
  const nextView = editor.getSnapshot().view;
  editor.panBy(
    viewportWidth / 2 - ((minX + maxX) / 2) * nextView.zoom - nextView.panX,
    viewportHeight / 2 - ((minY + maxY) / 2) * nextView.zoom - nextView.panY,
  );
}
